
"use client";

import * as React from "react";
import { useState, useCallback } from "react";
import { format, isToday, isTomorrow } from "date-fns";
import { useNavigate } from "react-router-dom";
import ServiceSelectionStep from "./ServiceSelectionStep";
import DateTimeSelectionStep from "./DateTimeSelectionStep";
import CustomerInfoPaymentStep from "@/components/booking-shared/CustomerInfoPaymentStep";
import { CustomerInfoPaymentData } from "@/components/booking-shared/CustomerInfoPaymentStep";
// Button import removed — FlowDock handles the CTA rendering
import { BusinessAnalytics } from '@/lib/posthog';
import { useBookingProcess } from "@/hooks/useBookingProcess";
import { ServiceType } from "@/types/booking";
import { findOrCreateClient, createBooking } from "@/utils/bookingDatabase";
import { DateTime } from "luxon";
import { toast } from "sonner";
import { getBookingTheme } from '@/components/booking-flow/themes';
import { type BuildRowData } from '@/components/booking-flow/FlowScaffold';
import { BookingFlowShell } from '@/components/booking-flow/BookingFlowShell';
import { type PassBooking } from '@/components/booking-flow/ConfirmationPass';
import { formatDuration, formatTimeRange } from '@/components/booking-flow/FlowSteps';

// Only log in development to avoid leaking PII to the browser console in production
const devLog = (...args: unknown[]) => { if (import.meta.env.DEV) console.log(...args); };

// --- INTERFACES ---

export interface Service {
  id: string;
  name: string;
  description: string;
  price: number;
  duration: number; // in minutes
  specialRequirements?: string;
}

export interface CustomerInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  specialRequests?: string;
  contactMethod: "email" | "phone";
  agreements: {
    marketing: boolean;
    reminders: boolean;
  };
  paymentMethod: string;
  cardName?: string;
  cardNumber?: string;
  cardExpiry?: string;
  cardCVC?: string;
}

// --- PROPS INTERFACE ---

interface BookingFlowProps {
  businessData?: {
    id: string;
    name: string;
    email: string | null;      // Nullable (opt-in via show_public_contact)
    phone: string | null;      // Nullable (opt-in via show_public_contact)
    ownerFirstName?: string | null; // Optional: business owner's first name
    website?: string;          // Public info (always safe)
    instagram?: string;        // Public info (always safe)
    timezone: string;
    welcomeMessage?: string;
    welcomeGif?: string;
    stripeAccountId?: string;
    stripeChargesEnabled?: boolean;
    bookingCardThemeKey?: string;
    bookingFlowThemeKey?: string;
    platformFeePercentage: number;
    enableTips: boolean;
    suggestedTipPercentages: number[];
    userId?: string;           // Internal use for booking operations
    title?: string | null;     // Tagline / profession (from onboarding)
    bio?: string | null;       // Short bio (from onboarding)
    logoUrl?: string | null;   // Business logo URL (from Storage)
    cancellationFeePolicy?: string | null; // Provider-authored cancellation policy
  };
  services?: Service[];
  businessHours?: {
    dayOfWeek: string;
    startTime: string;
    endTime: string;
    enabled: boolean;
  }[];
  /** Called when the user presses back on step 1 (e.g. return to link-in-bio) */
  onExit?: () => void;
}

// --- COMPONENT ---

export default function BookingFlow({ businessData, services, businessHours, onExit }: BookingFlowProps) {
  const theme = getBookingTheme(businessData?.bookingFlowThemeKey || 'figgy_purple');

  const {
    step: currentStep,
    bookingData,
    updateBookingData,
    nextStep,
    prevStep,
    goToStep,
  } = useBookingProcess();
  const navigate = useNavigate();

  const [navigationDirection, setNavigationDirection] = useState<'forward' | 'back'>('forward');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [tipAmount, setTipAmount] = useState(0);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfoPaymentData | null>(null);
  const [confirmStage, setConfirmStage] = useState<'confirming' | 'tray' | 'expanded'>('confirming');
  // Date picked on step 2 before a time is chosen (drives the CTA sub-line)
  const [pendingDate, setPendingDate] = useState<Date | undefined>(undefined);
  // Live payment-tile selection on step 3 (may precede a valid form) —
  // keeps the CTA sub-line honest while the customer is still typing
  const [livePaymentMethod, setLivePaymentMethod] = useState<string>(
    businessData?.stripeChargesEnabled ? 'new_card' : 'in_person'
  );

  const trackedBookingStartRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const businessId = businessData?.id;
    if (!businessId || trackedBookingStartRef.current === businessId) {
      return;
    }

    trackedBookingStartRef.current = businessId;
    BusinessAnalytics.bookingStarted(businessId, window.location.pathname);
  }, [businessData?.id]);

  // Gate the confirmation animation on REAL booking success.
  // The pass only advances to 'tray' (visible) once bookingData.bookingId exists,
  // preventing false-success display during payment-return race conditions.
  React.useEffect(() => {
    if (currentStep === 4) {
      if (bookingData.bookingId) {
        // Booking confirmed — run the animation sequence
        setConfirmStage('confirming');
        const timer = setTimeout(() => setConfirmStage('tray'), 1500);
        return () => clearTimeout(timer);
      } else {
        // Step 4 reached but booking not yet created (e.g., 3DS redirect in-flight).
        // Stay in 'confirming' (spinner/check animation) until bookingId arrives.
        setConfirmStage('confirming');
      }
    }
  }, [currentStep, bookingData.bookingId]);

  // Idempotency guard: ensure booking is only created once per payment
  const bookingCreatedRef = React.useRef(false);

  // Ref to always hold the latest callback (prevents stale closure)
  const handlePaymentSuccessCallbackRef = React.useRef<(isFromPaymentReturn?: boolean, paymentIntentId?: string) => Promise<void>>();

  // Check for payment success on component mount
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('payment_success');
    // Stripe includes payment_intent in URL when returning from redirect
    const paymentIntentFromUrl = urlParams.get('payment_intent');

    if (paymentSuccess === 'true') {
      devLog(`Payment success detected from redirect, payment_intent=${paymentIntentFromUrl}`);

      // Clear the URL parameters
      window.history.replaceState({}, '', window.location.pathname);

      // Restore booking state from localStorage
      const storedBookingState = localStorage.getItem("bookingStateForPayment");
      if (storedBookingState) {
        try {
          const bookingState = JSON.parse(storedBookingState);
          devLog('Restoring booking state:', bookingState);

          // Restore the booking data
          updateBookingData({
            service: bookingState.service,
            date: new Date(bookingState.date), // Convert back to Date object
            time: bookingState.time,
            customerName: bookingState.customerName,
            customerEmail: bookingState.customerEmail,
            customerPhone: bookingState.customerPhone,
            paymentMethod: bookingState.paymentMethod
          });

          // Clean up localStorage
          localStorage.removeItem("bookingStateForPayment");
          localStorage.removeItem("bookingReturnUrl");

          // Use ref to get latest callback with updated bookingData (prevents stale closure)
          // Pass payment_intent from URL or fallback to stored value
          const paymentIntentId = paymentIntentFromUrl || bookingState.paymentIntentId;
          setTimeout(() => {
            handlePaymentSuccessCallbackRef.current?.(true, paymentIntentId); // Pass payment intent ID from redirect
          }, 100);

          // Navigate directly to step 4 (confirmation) after booking creation
          setTimeout(() => {
            devLog('Navigating directly to confirmation step (step 4)...');
            goToStep(4);
          }, 2000); // Wait for booking creation to complete

        } catch (error) {
          console.error('Failed to restore booking state:', error);
          setSubmitError("Failed to restore booking information. Please try again.");
        }
      } else {
        console.error('No stored booking state found for payment success');
        setSubmitError("Booking information was lost. Please start over.");
      }
    }
  }, []); // Empty deps OK - ref pattern prevents stale closure

  const handlePaymentSuccessCallback = useCallback(async (isFromPaymentReturn = false, paymentIntentId?: string) => {
    const correlationId = paymentIntentId || 'IN_PERSON';
    devLog(`[PAYMENT-FLOW:${correlationId}] [5/7] handlePaymentSuccessCallback START`);
    devLog(`[PAYMENT-FLOW:${correlationId}] isFromPaymentReturn:`, isFromPaymentReturn);
    devLog(`[PAYMENT-FLOW:${correlationId}] Full booking data:`, bookingData);
    devLog(`[PAYMENT-FLOW:${correlationId}] Customer info state:`, customerInfo);
    devLog(`[PAYMENT-FLOW:${correlationId}] Business data:`, businessData);

    // Use customerInfo state directly to avoid React state timing issues
    const customerName = customerInfo ? `${customerInfo.firstName} ${customerInfo.lastName}` : bookingData.customerName;
    const customerEmail = customerInfo?.email || bookingData.customerEmail;
    const customerPhone = customerInfo?.phone || bookingData.customerPhone;

    if (!businessData || !bookingData.service || !bookingData.date || !bookingData.time || !customerName || !customerEmail) {
      console.error(`[PAYMENT-FLOW:${correlationId}] [6/7] BOOKING VALIDATION FAILED - Missing required booking information:`, {
        businessData: !!businessData,
        service: !!bookingData.service,
        date: !!bookingData.date,
        time: !!bookingData.time,
        customerName: !!customerName,
        customerEmail: !!customerEmail,
        hasCustomerInfo: !!customerInfo
      });
      setSubmitError("Missing required booking information");
      devLog(`[PAYMENT-FLOW:${correlationId}] Returning early due to validation failure`);
      return;
    }

    devLog(`[PAYMENT-FLOW:${correlationId}] Validation passed, proceeding with booking creation`);

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Step 1: Create or find client for SMS/CRM
      devLog(`[PAYMENT-FLOW:${correlationId}] [6/7a] Creating client record...`);
      const firstName = customerInfo?.firstName || customerName.split(' ')[0] || '';
      const lastName = customerInfo?.lastName || customerName.split(' ').slice(1).join(' ') || '';

      const clientResult = await findOrCreateClient({
        firstName,
        lastName,
        email: customerEmail,
        phone: customerPhone,
        businessId: businessData.id
      });

      if (!clientResult.success) {
        console.warn(`[PAYMENT-FLOW:${correlationId}] Client creation failed (non-blocking):`, clientResult.error);
      }

      // Step 2: Create booking with customer snapshot + client_id if available
      // RLS policy allows public booking creation with payment_intent_id validation
      devLog(`[PAYMENT-FLOW:${correlationId}] [6/7b] Creating booking...`);

      const bookingResult = await createBooking({
        businessId: businessData.id,
        serviceId: bookingData.service.id,
        clientId: clientResult.clientId,  // Link to client for SMS/CRM if available
        bookingDate: bookingData.date,
        bookingTime: bookingData.time,
        duration: bookingData.service.duration,
        specialRequests: "", // TODO: Add special requests field to booking flow
        // Customer snapshot fields (required for RLS policy) - use local variables from customerInfo
        customerName,
        customerEmail,
        customerPhone,
        // Only store real payment intent IDs, not fallback placeholders
        paymentIntentId: (correlationId !== 'IN_PERSON' && correlationId !== 'UNKNOWN') ? correlationId : undefined,
      });

      if (!bookingResult.success) {
        const errorMsg = bookingResult.error || "Failed to create booking";
        console.error(`[PAYMENT-FLOW:${correlationId}] [6/7] Booking creation FAILED:`, {
          success: bookingResult.success,
          hasBookingId: !!bookingResult.bookingId,
          error: errorMsg
        });
        setSubmitError(errorMsg);
        setIsSubmitting(false);
        throw new Error(errorMsg); // Throw so caller knows it failed
      }

      devLog(`[PAYMENT-FLOW:${correlationId}] [6/7] Booking created successfully:`, {
        bookingId: bookingResult.bookingId,
        confirmationNumber: bookingResult.confirmationNumber,
        clientId: clientResult.clientId
      });

      // Step 3: Update booking data and proceed to confirmation
      updateBookingData({
        paymentComplete: true,
        bookingId: bookingResult.bookingId
      });

      // Update the confirmation number in the booking process
      if (bookingResult.confirmationNumber) {
        updateBookingData({
          confirmationNumber: bookingResult.confirmationNumber
        });
      }

      // Step 4: SMS confirmation is now handled by booking-confirmation edge function
      // (triggered automatically by createBooking via database trigger)
      // No need for duplicate SMS sending here

      devLog(`[PAYMENT-FLOW:${correlationId}] [7/7] Navigation to confirmation...`);
      // Only call nextStep if this is NOT from a payment return (i.e., in-person booking)
      if (!isFromPaymentReturn) {
        nextStep();
        devLog(`[PAYMENT-FLOW:${correlationId}] [7/7] ✓ COMPLETE - navigated to confirmation step`);
      } else {
        devLog(`[PAYMENT-FLOW:${correlationId}] [7/7] ✓ COMPLETE - skipping navigation (payment return flow)`);
      }

    } catch (error) {
      console.error(`[PAYMENT-FLOW:${correlationId}] [6/7] BOOKING CREATION EXCEPTION:`, error);

      // Determine if this is a post-payment failure (critical - user was charged)
      const isPostPayment = correlationId !== 'IN_PERSON' && correlationId !== 'UNKNOWN';

      if (isPostPayment) {
        // CRITICAL: Payment captured but booking failed - needs manual reconciliation
        console.error(`[PAYMENT-FLOW:${correlationId}] [CRITICAL] PAYMENT CAPTURED BUT BOOKING FAILED`, {
          payment_intent_id: correlationId,
          customer_email: bookingData.customerEmail,
          customer_phone: bookingData.customerPhone,
          business_id: businessData.id,
          service_id: bookingData.service.id,
          booking_date: bookingData.date,
          booking_time: bookingData.time,
          error: error instanceof Error ? error.message : String(error)
        });

        setSubmitError(
          "Your payment was successful, but we encountered an error saving your booking. " +
          "Please contact support immediately with confirmation code: " + correlationId.slice(-8)
        );
      } else {
        setSubmitError("An unexpected error occurred while creating your booking. Please try again.");
      }

      // Re-throw so caller knows booking creation failed - prevents false-success state
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [businessData, bookingData, customerInfo, updateBookingData, nextStep]);

  // Assign ref synchronously during render (not in useEffect) to ensure it's available
  // for payment return flow that runs on mount. This prevents race condition where
  // payment return useEffect tries to call ref before it's been assigned.
  handlePaymentSuccessCallbackRef.current = handlePaymentSuccessCallback;

  // Progressive BuildPass rows \u2014 mirrors the design's flow.jsx:
  // only ever show what's actually been selected.
  const relativeDayLabel = (d: Date) =>
    isToday(d) ? 'Today' : isTomorrow(d) ? 'Tomorrow' : format(d, 'EEEE, MMM d');

  const buildRows: BuildRowData[] = [];
  if (bookingData.service) {
    buildRows.push({
      label: 'Service',
      value: bookingData.service.name,
      sub: `${formatDuration(bookingData.service.duration)} \u00b7 $${bookingData.service.price}`,
      state: 'filled',
    });
  }
  if (bookingData.date && bookingData.time && currentStep >= 3) {
    buildRows.push({
      label: 'When',
      value: relativeDayLabel(bookingData.date),
      sub: formatTimeRange(bookingData.time, bookingData.service?.duration || 60),
      state: 'filled',
    });
  }
  const guest = bookingData.customerName?.trim();
  if (guest && currentStep >= 3) {
    buildRows.push({ label: 'Guest', value: guest, state: 'filled' });
  }

  // Confirmation pass booking data — only populated once booking is actually confirmed
  const passBooking: PassBooking | null = (bookingData.bookingId && bookingData.service && bookingData.date && bookingData.time) ? {
    service: bookingData.service.name,
    dateNum: format(bookingData.date, 'MMM d'),
    dateDay: format(bookingData.date, 'EEEE'),
    time: formatTimeRange(bookingData.time, bookingData.service.duration),
    duration: formatDuration(bookingData.service.duration),
    guest: bookingData.customerName?.trim() || null,
    withName: businessData?.ownerFirstName || null,
    confirmation: bookingData.confirmationNumber || '',
  } : null;

  const handleNextStep = useCallback(() => {
    setNavigationDirection('forward');
    nextStep();
  }, [nextStep]);

  const handlePrevStep = useCallback(() => {
    setNavigationDirection('back');
    if (currentStep <= 1) {
      onExit?.();
      return;
    }
    prevStep();
  }, [currentStep, prevStep, onExit]);

  const isNextDisabled = (): boolean => {
    switch (currentStep) {
      case 1:
        return !bookingData.service;
      case 2:
        return !bookingData.date || !bookingData.time;
      case 3:
        // The dock CTA creates an in-person booking directly (it does not
        // submit the details form), so zod's termsAccepted check never runs
        // on this path — terms acceptance must gate the CTA here.
        return (
          !bookingData.customerName ||
          !bookingData.customerEmail ||
          (livePaymentMethod === "in_person" &&
            !customerInfo?.agreements?.termsAccepted)
        );
      default:
        return false;
    }
  };

  const trackedServiceRef = React.useRef<string | null>(null);

  const handleServiceSelection = useCallback((service: Service) => {
    // Convert Service to ServiceType format
    const serviceType: ServiceType = {
      id: service.id,
      name: service.name,
      description: service.description,
      price: service.price,
      duration: service.duration
    };

    updateBookingData({ service: serviceType });

    if (businessData?.id && trackedServiceRef.current !== service.id) {
      trackedServiceRef.current = service.id;
      BusinessAnalytics.serviceSelected(
        service.id,
        businessData.id,
        service.price,
        service.duration
      );
    }

    // Only auto-advance when moving forward
    if (navigationDirection === 'forward') {
      setTimeout(() => {
        nextStep();
      }, 360);
    }
  }, [navigationDirection, updateBookingData, nextStep, businessData?.id]);

  const handleDateTimeSelection = useCallback((dateTime: {
    date: Date;
    time: string;
  } | null) => {
    if (dateTime) {
      updateBookingData({ 
        date: dateTime.date, 
        time: dateTime.time 
      });
      
      // Only auto-advance when moving forward
      if (navigationDirection === 'forward') {
        setTimeout(() => {
          nextStep();
        }, 360);
      }
    }
  }, [navigationDirection, updateBookingData, nextStep]);

  const handleCustomerInfoUpdate = useCallback((info: CustomerInfoPaymentData | null) => {
    setCustomerInfo(info);
    if (info) {
      updateBookingData({
        customerName: `${info.firstName} ${info.lastName}`,
        customerEmail: info.email,
        customerPhone: info.phone || "",
        paymentMethod: info.paymentMethod === "new_card" ? "credit_card" : "in_person"
      });

      // Persist booking state for 3DS redirect recovery (/payment-success page)
      if (info.paymentMethod === "new_card" && businessData && bookingData.service && bookingData.date && bookingData.time) {
        try {
          sessionStorage.setItem("figgy_pending_booking", JSON.stringify({
            businessId: businessData.id,
            returnUrl: window.location.href,
            serviceId: bookingData.service.id,
            serviceDuration: bookingData.service.duration,
            bookingDate: bookingData.date.toISOString(),
            bookingTime: bookingData.time,
            customerFirstName: info.firstName,
            customerLastName: info.lastName,
            customerEmail: info.email,
            customerPhone: info.phone || "",
            savedAt: Date.now(),
          }));
        } catch (e) {
          console.warn("[BookingFlow] Could not write pending booking to sessionStorage:", e);
        }
      }
    }
  }, [updateBookingData, businessData, bookingData.service, bookingData.date, bookingData.time]);

  const handlePaymentSuccess = useCallback((paymentData?: {
    stripeCustomerId?: string;
    paymentMethodId?: string;
    paymentIntentId?: string;
  }) => {
    const correlationId = paymentData?.paymentIntentId || 'UNKNOWN';
    devLog(`[PAYMENT-FLOW:${correlationId}] [4/7] handlePaymentSuccess called with payment data:`, paymentData);
    devLog(`[PAYMENT-FLOW:${correlationId}] Current booking state:`, {
      paymentMethod: bookingData.paymentMethod,
      service: bookingData.service?.name,
      date: bookingData.date,
      time: bookingData.time,
      customerName: bookingData.customerName,
      customerEmail: bookingData.customerEmail
    });

    updateBookingData({ paymentComplete: true });

    toast.success("Payment successful! Processing booking...");

    // Dispatch event to trigger booking creation
    devLog(`[PAYMENT-FLOW:${correlationId}] [4/7] Dispatching customer-info-payment-success event...`);
    const event = new CustomEvent('customer-info-payment-success', {
      detail: { paymentIntentId: correlationId }
    });
    window.dispatchEvent(event);
  }, [updateBookingData, bookingData]);

  const handlePaymentError = useCallback((error: string) => {
    setSubmitError(error);
    toast.error("Payment failed: " + error);
  }, []);

  // Listen for payment success event to trigger booking creation
  // Listener is registered for entire step 3, independent of form validity
  React.useEffect(() => {
    if (currentStep === 3) {
      const handler = async (event: Event) => {
        const customEvent = event as CustomEvent<{ paymentIntentId: string }>;
        const correlationId = customEvent.detail?.paymentIntentId || 'UNKNOWN';

        devLog(`[PAYMENT-FLOW:${correlationId}] [5/7] Payment success event received in BookingFlow`);
        devLog(`[PAYMENT-FLOW:${correlationId}] Payment method:`, bookingData.paymentMethod);
        devLog(`[PAYMENT-FLOW:${correlationId}] Booking created ref:`, bookingCreatedRef.current);

        // Only process credit card payments, and only once (idempotency)
        if (bookingData.paymentMethod === "credit_card" && !bookingCreatedRef.current) {
          devLog(`[PAYMENT-FLOW:${correlationId}] [5/7] Processing booking creation...`);
          try {
            await handlePaymentSuccessCallback(false, correlationId);
            // Only set ref after successful completion - allows retry on failure
            bookingCreatedRef.current = true;
            devLog(`[PAYMENT-FLOW:${correlationId}] Booking created successfully, idempotency guard set`);
          } catch (error) {
            // Ref stays false, allowing user to retry
            console.error(`[PAYMENT-FLOW:${correlationId}] [6/7] Booking creation failed, ref not set - retry allowed:`, error);
          }
        } else {
          devLog(`[PAYMENT-FLOW:${correlationId}] Skipping booking creation:`, {
            isNotCreditCard: bookingData.paymentMethod !== "credit_card",
            alreadyCreated: bookingCreatedRef.current
          });
        }
      };
      window.addEventListener('customer-info-payment-success', handler);
      return () => window.removeEventListener('customer-info-payment-success', handler);
    }
  }, [currentStep, bookingData.paymentMethod, handlePaymentSuccessCallback]);

  // Reset idempotency guard when navigating away from step 3
  React.useEffect(() => {
    if (currentStep !== 3) {
      bookingCreatedRef.current = false;
    }
  }, [currentStep]);



  const handleInPersonBooking = useCallback(async () => {
    // This is the original handlePayment function for in-person bookings
    // Use customerInfo state directly to avoid React state timing issues
    const customerName = customerInfo ? `${customerInfo.firstName} ${customerInfo.lastName}` : bookingData.customerName;
    const customerEmail = customerInfo?.email || bookingData.customerEmail;
    const customerPhone = customerInfo?.phone || bookingData.customerPhone;

    if (!businessData || !bookingData.service || !bookingData.date || !bookingData.time || !customerName || !customerEmail) {
      setSubmitError("Missing required booking information");
      return;
    }

    // Hard guard: this handler is reachable from the dock CTA without a form
    // submit, so the zod termsAccepted refinement never runs on this path.
    if (!customerInfo?.agreements?.termsAccepted) {
      setSubmitError("You must accept the Terms of Service and Privacy Policy");
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Extract first and last name from customerInfo or fallback to bookingData
      const firstName = customerInfo?.firstName || customerName.split(' ')[0] || '';
      const lastName = customerInfo?.lastName || customerName.split(' ').slice(1).join(' ') || '';

      // Step 1: Find or create client
      const clientResult = await findOrCreateClient({
        firstName,
        lastName,
        email: customerEmail,
        phone: customerPhone,
        businessId: businessData.id
      });

      if (!clientResult.success) {
        console.warn(`[IN-PERSON-FLOW] Client creation failed (non-blocking):`, clientResult.error);
      }

      // Step 2: Create booking with customer snapshot
      const bookingResult = await createBooking({
        businessId: businessData.id,
        serviceId: bookingData.service.id,
        clientId: clientResult.clientId,
        bookingDate: bookingData.date,
        bookingTime: bookingData.time,
        duration: bookingData.service.duration,
        specialRequests: "", // TODO: Add special requests field to booking flow
        // Customer snapshot fields (required for consistency)
        customerName,
        customerEmail,
        customerPhone,
        // paymentId will be added later when payment processing is implemented
      });

      if (!bookingResult.success) {
        setSubmitError(bookingResult.error || "Failed to create booking");
        setIsSubmitting(false);
        return;
      }

      // Step 3: Update booking data and proceed to confirmation
      updateBookingData({ 
        paymentComplete: true,
        bookingId: bookingResult.bookingId 
      });
      
      // Update the confirmation number in the booking process
      if (bookingResult.confirmationNumber) {
        updateBookingData({ 
          confirmationNumber: bookingResult.confirmationNumber 
        });
      }
      
      nextStep();

    } catch (error) {
      console.error('Error creating booking:', error);
      setSubmitError("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [businessData, bookingData, customerInfo, updateBookingData, nextStep]);

  const renderStepContent = () => {
    const selectedService = bookingData.service ? {
      id: bookingData.service.id,
      name: bookingData.service.name,
      description: bookingData.service.description,
      price: bookingData.service.price,
      duration: bookingData.service.duration,
      specialRequirements: bookingData.service.waitlistEnabled ? "Waitlist available" : undefined
    } : null;

    const selectedDateTime = bookingData.date && bookingData.time ? {
      date: bookingData.date,
      time: bookingData.time
    } : null;

    // Seed the details step from the live form state when we have it (it holds
    // the full agreements the user actually made — terms, marketing). The
    // reconstruction from bookingData below is only a fallback (e.g. state
    // restored after a payment redirect) and deliberately resets termsAccepted
    // to false: consent must never be presumed, and seeding it stale-true here
    // would let the CTA's terms gate pass while the remounted checkbox renders
    // unchecked.
    const customerInfoPaymentData: CustomerInfoPaymentData | null = customerInfo ?? (bookingData.customerName ? {
      firstName: bookingData.customerName.split(' ')[0] || "",
      lastName: bookingData.customerName.split(' ').slice(1).join(' ') || "",
      email: bookingData.customerEmail,
      phone: bookingData.customerPhone || "",
      specialRequests: "",
      contactMethod: "email" as const,
      agreements: {
        marketing: false,
        reminders: true,
        termsAccepted: false
      },
      paymentMethod: bookingData.paymentMethod === "credit_card" ? "new_card" : "in_person"
    } : null);

    switch (currentStep) {
      case 1:
        return <ServiceSelectionStep 
          selectedService={selectedService} 
          onSelectService={handleServiceSelection} 
          services={services}
        />;
      case 2:
        return <DateTimeSelectionStep 
          serviceDuration={bookingData.service?.duration || 60} 
          selectedDateTime={selectedDateTime} 
          onSelectDateTime={handleDateTimeSelection} 
          businessId={businessData?.id}
          onDateChange={setPendingDate}
        />;
      case 3:
        return <CustomerInfoPaymentStep
          customerInfo={customerInfoPaymentData}
          onUpdateCustomerInfo={handleCustomerInfoUpdate}
          selectedService={bookingData.service}
          tipAmount={tipAmount}
          onTipChange={setTipAmount}
          businessId={businessData?.id}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentError={handlePaymentError}
          hasDateTimeSelected={!!(bookingData.date && bookingData.time)}
          cardPaymentsEnabled={!!businessData?.stripeChargesEnabled}
          cancellationPolicy={businessData?.cancellationFeePolicy || null}
          onPaymentMethodChange={setLivePaymentMethod}
        />;
      case 4:
        return null; // Confirmation is handled by ConfirmScene outside renderStepContent
      default:
        return null;
    }
  };

  // ── Per-step page header copy (from the design's screens.jsx) ──
  const STEP_HEADERS: Record<number, { title: string }> = {
    1: { title: 'Select a service' },
    2: { title: 'Pick a date' },
    3: { title: 'Your details' },
  };

  // ── Contextual CTA sub-line under the dock button ──
  const ctaSubForStep = (): string | undefined => {
    switch (currentStep) {
      case 1:
        return bookingData.service
          ? `${formatDuration(bookingData.service.duration)} \u00b7 $${bookingData.service.price}`
          : 'Select a service to continue';
      case 2:
        if (bookingData.date && bookingData.time) {
          return `${relativeDayLabel(bookingData.date)} at ${bookingData.time}`;
        }
        return (bookingData.date || pendingDate) ? 'Pick a time to continue' : 'Pick a date to see available times';
      case 3:
        // Card payments submit through Stripe's own button; only promise
        // in-person payment when that's actually the live selection
        return livePaymentMethod === 'in_person'
          ? 'Nothing due now — pay in person at your visit.'
          : undefined;
      default:
        return undefined;
    }
  };

  const flowStep = Math.min(currentStep, 3);
  // Defensive fallback: flowStep is clamped to 1-3, but a bad step value
  // must never white-screen the flow on the title lookup
  const header = STEP_HEADERS[flowStep] ?? STEP_HEADERS[1];
  const showDock = !(flowStep === 3 && bookingData.paymentMethod === "credit_card");
  const showConfirmation = currentStep >= 4;

  return (
    <BookingFlowShell
      theme={theme}
      businessName={businessData?.name || 'Business'}
      step={flowStep}
      totalSteps={3}
      title={header.title}
      buildRows={buildRows}
      onBack={handlePrevStep}
      showDock={showDock}
      cta={isSubmitting ? 'Creating booking...' : flowStep === 3 ? `Confirm booking \u00b7 $${bookingData.service?.price || ''}` : 'Continue'}
      ctaDisabled={isNextDisabled() || isSubmitting}
      ctaSub={ctaSubForStep()}
      onCta={flowStep === 3 ? handleInPersonBooking : handleNextStep}
      error={submitError}
      noSnapshotKeys={['step-3']}
      showConfirmation={showConfirmation}
      confirmStage={confirmStage}
      confirmBooking={passBooking}
      onConfirmTap={() => setConfirmStage(s => s === 'tray' ? 'expanded' : s)}
      onConfirmClose={() => setConfirmStage('tray')}
      onManage={bookingData.bookingId ? () => navigate(`/booking/confirmation/${bookingData.bookingId}`) : undefined}
      onAddToCalendar={() => {
        if (!bookingData.service || !bookingData.date || !bookingData.time) return;
        const startTime = new Date(bookingData.date);
        const [time, period] = bookingData.time.split(' ');
        let [hours] = time.split(':').map(Number);
        const [, minutes] = time.split(':').map(Number);
        if (period === 'PM' && hours < 12) hours += 12;
        if (period === 'AM' && hours === 12) hours = 0;
        startTime.setHours(hours, minutes);
        const endTime = new Date(startTime.getTime() + bookingData.service.duration * 60000);
        const fmtDate = (d: Date) => d.toISOString().replace(/-|:|\.\d\d\d/g, '');
        const url = new URL('https://www.google.com/calendar/render');
        url.searchParams.append('action', 'TEMPLATE');
        url.searchParams.append('text', `Booking: ${bookingData.service.name}`);
        url.searchParams.append('dates', `${fmtDate(startTime)}/${fmtDate(endTime)}`);
        url.searchParams.append('details', `Service: ${bookingData.service.description}${businessData?.name ? `\nBusiness: ${businessData.name}` : ''}${businessData?.phone ? `\nPhone: ${businessData.phone}` : ''}`);
        if (businessData?.name) url.searchParams.append('location', businessData.name);
        window.open(url.toString(), '_blank');
      }}
      businessTagline={businessData?.title || null}
    >
      {renderStepContent()}
    </BookingFlowShell>
  );
}
